import { useLingui as _useLingui } from "../lib/i18n/useLingui";
import { ALIGN_JUSTIFY_CLASS, ALIGN_TEXT_CLASS, DATA_TABLE_HEADER_HEIGHT_PX, DATA_TABLE_ROW_HEIGHT_PX, getDataTableColumnPresets } from "../components/DataTable";
import { Icon } from "../components/Icon";
import { ChevronSelectorVertical } from "../icons/v2-stroke";
import { cn } from "../lib/utils";
import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import React from "react";
// A new `data` array on every render makes TanStack auto-reset the page index in a microtask, whose
// state update re-renders the skeleton with yet another array: an endless loop that freezes the page.
const NO_ROWS = [];

/**
 * A loading table that reuses the loaded table's column definitions and requires
 * a custom SkeletonCell renderer. Keep that renderer alongside the table's columns
 * and compose TextCellSkeleton, AvatarCellSkeleton, or ChipCellSkeleton to match
 * their contents. Use LoadingBlock for other shapes or more specialized layouts.
 * Preserve literal column ids with `as const` and `satisfies ColumnDef<...>[]`,
 * then use `(typeof columns)[number]["id"]` to type an exhaustive cell renderer.
 */
export function DataTableSkeleton({
  columns,
  SkeletonCell,
  rowCount = 10,
  density = "default",
  rowHeight = DATA_TABLE_ROW_HEIGHT_PX[density]
}) {
  const {
    _: _t
  } = _useLingui();
  const table = useReactTable({
    data: NO_ROWS,
    columns,
    getCoreRowModel: getCoreRowModel()
  });
  return /*#__PURE__*/React.createElement("div", {
    className: "@container/table"
  }, /*#__PURE__*/React.createElement("table", {
    "aria-label": _t(
    /** i18n */
    {
      id: "Ko2wWN",
      message: "Loading table"
    }),
    "aria-busy": "true",
    className: "w-full table-fixed border-collapse"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", {
    className: "border-b border-separator"
  }, table.getFlatHeaders().map(header => {
    var _header$column$column;
    const presets = getDataTableColumnPresets(header.column);
    return /*#__PURE__*/React.createElement("th", {
      key: header.id,
      scope: "col",
      style: {
        height: DATA_TABLE_HEADER_HEIGHT_PX[density]
      },
      className: cn("heading-sm px-2 text-foreground", ALIGN_TEXT_CLASS[presets.headerAlign], presets.headerClassName, (_header$column$column = header.column.columnDef.meta) === null || _header$column$column === void 0 ? void 0 : _header$column$column.className)
    }, /*#__PURE__*/React.createElement("div", {
      className: cn("flex items-center gap-1 whitespace-nowrap", presets.headerAlign !== "left" && ALIGN_JUSTIFY_CLASS[presets.headerAlign])
    }, flexRender(header.column.columnDef.header, header.getContext()), header.column.getCanSort() && presets.sortable && /*#__PURE__*/React.createElement(Icon, {
      visual: ChevronSelectorVertical,
      size: "xs"
    })));
  }))), /*#__PURE__*/React.createElement("tbody", {
    "aria-hidden": "true"
  }, Array.from({
    length: rowCount
  }, (_, rowIndex) => /*#__PURE__*/React.createElement("tr", {
    key: rowIndex,
    className: "border-b border-separator"
  }, table.getAllLeafColumns().map(column => {
    var _column$columnDef$met;
    return /*#__PURE__*/React.createElement("td", {
      key: column.id,
      className: cn("px-2", getDataTableColumnPresets(column).cellClassName, (_column$columnDef$met = column.columnDef.meta) === null || _column$columnDef$met === void 0 ? void 0 : _column$columnDef$met.className),
      style: {
        height: rowHeight
      }
    }, /*#__PURE__*/React.createElement(SkeletonCell
    // TanStack widens ids to string. Narrow ids require flat
    // columns with explicit ids, which TanStack preserves.
    , {
      columnId: column.id,
      rowIndex: rowIndex
    }));
  }))))));
}
//# sourceMappingURL=DataTableSkeleton.js.map